import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminNavBar from "../components/AdminNavBar";
import { useAdminAcademicResources } from "../store/admin";
import StaffPagination from "./components/staff/StaffPagination";
import {
  Download,
  BookOpen,
  FileText,
  ClipboardList,
  Wallet,
  Book,
  Eye,
} from "lucide-react";



/** Lucide icon + label per resource kind. */
const TYPE_META = {
  book: { label: "Book", icon: Book },
  course: { label: "Course", icon: BookOpen },
  journal: { label: "Journal", icon: FileText },
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

/** Collapse the various API status values into the three UI buckets. */
function mapStatus(raw) {
  const s = String(raw || "APPROVED").toUpperCase();
  if (s === "DRAFT") return "Draft";
  if (["PENDING", "PENDING_REVIEW", "UNDER_REVIEW"].includes(s)) return "Pending";
  if (["REJECTED", "DISABLED", "ARCHIVED", "SUSPENDED", "WITHDRAWN"].includes(s)) return "Archived";
  if (["APPROVED", "ACTIVE", "PUBLISHED"].includes(s)) return "Active";
  return capitalize(s);
}

const formatMoney = (value) =>
  value == null
    ? null
    : `₦${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * The list items are not flat: `category`, `tutor`, `adminModerator` and
 * `staffModerator` are nested objects (see `GET /api/admin/courses`), so a
 * plain `raw.category` would render an object as a React child. Normalise
 * every author-ish field down to a displayable string here.
 */
function authorOf(raw) {
  return (
    raw?.author ||
    raw?.tutor?.name ||
    raw?.creator?.name ||
    raw?.createdBy?.name ||
    raw?.creatorName ||
    raw?.instructorName ||
    raw?.uploaderName ||
    "—"
  );
}

/** `category` may be `{ id, name }`, a bare id, or a plain string. */
function categoryOf(raw) {
  const category = raw?.category;
  if (category && typeof category === "object") return category.name || "";
  return typeof category === "string" ? category : raw?.categoryName || "";
}

/**
 * Map a `/api/admin/books|courses|journals` record into the row shape the
 * table below renders.
 */
function mapResource(kind, raw) {
  const meta = TYPE_META[kind] || TYPE_META.book;
  const coverImage = raw.coverImage || raw.image || raw.coverImageUrl || raw.thumbnail || null;
  const downloads = raw.downloads ?? raw.downloadCount ?? raw.totalDownloads ?? raw.enrollmentCount ?? null;
  const revenue = raw.revenue ?? raw.totalRevenue ?? raw.earnings ?? null;
  const updated = raw.updatedAt || raw.createdAt || raw.uploadedAt;
  const code =
    categoryOf(raw) || raw.code || raw.department || raw.subject || kind.toUpperCase();
  return {
    id: `${kind}-${raw.id}`,
    // Kept separate so the approve/reject buttons can hit the right endpoint
    // (`/api/admin/books/:id/approve` vs `…/courses/:id/approve`).
    kind,
    rawId: raw.id,
    title: raw.title || raw.name || "Untitled",
    meta: `${code} • ${updated ? `Updated ${new Date(updated).toLocaleDateString()}` : "—"}`,
    author: authorOf(raw),
    type: meta.label,
    typeIcon: meta.icon,
    status: mapStatus(raw.status),
    price: formatMoney(raw.price) ?? "Free",
    downloads: downloads != null ? Number(downloads).toLocaleString() : "—",
    revenue: formatMoney(revenue) ?? "₦0",
    cover: coverImage
      ? { type: "image", src: coverImage }
      : { type: "icon", icon: meta.icon, tone: "" },
  };
}

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Pending: "bg-[#dae2fd] text-[#565e74]",
  Draft: "bg-slate-100 text-slate-600",
  Archived: "bg-[#e0e3e5] text-[#434655]",
};

// Mock contributors / activity / stat cards removed — every figure on this
// page is now derived from the live admin API.

const AcademicResources = () => {
  const navigate = useNavigate();
  const {
    books,
    courses,
    journals,
    booksLoading,
    coursesLoading,
    journalsLoading,
    booksTotal,
    coursesTotal,
    journalsTotal,
    booksError,
    coursesError,
    journalsError,
    booksPages,
    coursesPages,
    journalsPages,
    fetchBooks,
    fetchCourses,
    fetchJournals,
  } = useAdminAcademicResources();

  // Load books, courses and journals from the admin API on mount and whenever
  // the page changes. The list envelope is `{ items, total, page, limit,
  // totalPages }`, so the backend paginates — pass `page`/`limit` through.
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  // "all" | "book" | "course" | "journal" — the stat cards double as filters.
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    const params = {
      page,
      limit,
      sort,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    };
    fetchBooks(params);
    fetchCourses(params);
    fetchJournals(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, statusFilter, sort]);

  const isLoading = booksLoading || coursesLoading || journalsLoading;
  const errorMessage = booksError || coursesError || journalsError;

  const liveResources = useMemo(
    () => [
      ...(books || []).map((r) => mapResource("book", r)),
      ...(courses || []).map((r) => mapResource("course", r)),
      ...(journals || []).map((r) => mapResource("journal", r)),
    ],
    [books, courses, journals]
  );

  // Academic resources are rendered straight from the admin API, filtered by
  // the stat card the admin selected (All / Books / Journals / Courses).
  const resources = useMemo(
    () =>
      typeFilter === "all"
        ? liveResources
        : liveResources.filter((resource) => resource.kind === typeFilter),
    [liveResources, typeFilter]
  );

  const activePages =
    typeFilter === "book"
      ? booksPages
      : typeFilter === "course"
        ? coursesPages
        : typeFilter === "journal"
          ? journalsPages
          : Math.max(booksPages, journalsPages, coursesPages, 1);

  const activeTotal =
    typeFilter === "book"
      ? booksTotal
      : typeFilter === "course"
        ? coursesTotal
        : typeFilter === "journal"
          ? journalsTotal
          : (booksTotal ?? 0) + (journalsTotal ?? 0) + (coursesTotal ?? 0);

  const selectType = (nextFilter) => {
    setTypeFilter(nextFilter);
    setPage(1);
  };

  // Moderation now happens on a dedicated detail page so the admin can read
  // the full submission before deciding. This just routes to it.
  const openDetail = (row) => navigate(`/admin/resources/${row.kind}/${row.rawId}`);

  // Stat cards are derived from the live counts (no static figures). The
  // `*Total` values come from the envelope's `total`, which is the true
  // server-side count — never `items.length`, which is capped by `limit`.
  const statCards = useMemo(
    () => [
      {
        label: "Books",
        filter: "book",
        value: (booksTotal ?? books.length).toLocaleString(),
        delta: "Live",
        deltaTone: "bg-[#eceef0] text-[#434655]",
        icon: BookOpen,
        iconBg: "bg-[#004ac6]/10",
        iconColor: "text-[#004ac6]",
      },
      {
        label: "Journals",
        filter: "journal",
        value: (journalsTotal ?? journals.length).toLocaleString(),
        delta: "Live",
        deltaTone: "bg-[#eceef0] text-[#434655]",
        icon: FileText,
        iconBg: "bg-[#dae2fd]/30",
        iconColor: "text-[#565e74]",
      },
      {
        label: "Courses",
        filter: "course",
        value: (coursesTotal ?? courses.length).toLocaleString(),
        delta: "Live",
        deltaTone: "bg-[#eceef0] text-[#434655]",
        icon: ClipboardList,
        iconBg: "bg-[#d3e4fe]",
        iconColor: "text-[#46566c]",
      },
      {
        label: "Total Items",
        filter: "all",
        value: (booksTotal ?? 0) + (journalsTotal ?? 0) + (coursesTotal ?? 0) || resources.length,
        delta: "Live",
        deltaTone: "bg-[#eceef0] text-[#434655]",
        icon: Wallet,
        iconBg: "bg-[#b4c5ff]",
        iconColor: "text-[#003ea8]",
      },
    ],
    [books, journals, courses, booksTotal, journalsTotal, coursesTotal, resources.length]
  );

  return (
    <div className="min-h-full bg-slate-50">
      <AdminNavBar title="Academic Resources" />
      <main className="md:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
          <div className="flex-1 overflow-y-auto p-4 bg-[#f7f9fb]">
            {/* Page Header */}
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  Academic Resources
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                Manage books, journals, projects, and seminar publications.
              </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const rows = [
                      ["Title", "Type", "Author", "Status", "Price", "Downloads", "Revenue"],
                      ...resources.map((r) => [
                        r.title,
                        r.type,
                        r.author,
                        r.status,
                        r.price,
                        r.downloads,
                        r.revenue,
                      ]),
                    ];
                    const csv = rows
                      .map((row) =>
                        row
                          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
                          .join(",")
                      )
                      .join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "academic-resources.csv";
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Bento Grid Stats */}
            <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {statCards.map((card) => {
                const Icon = card.icon;
                const active = typeFilter === card.filter;
                return (
                  <button
                    key={card.label}
                    type="button"
                    onClick={() => selectType(card.filter)}
                    aria-pressed={active}
                    title={`Show ${card.label.toLowerCase()}`}
                    className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                      active
                        ? "border-[#004ac6] ring-2 ring-[#004ac6]/20"
                        : "border-slate-200 hover:border-[#004ac6]/50"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className={`p-2 ${card.iconBg} rounded-lg`}>
                        <Icon className={`w-5 h-5 ${card.iconColor}`} />
                      </div>
                      <span className={`text-[10px] font-bold uppercase ${card.deltaTone} px-2 py-[2px] rounded`}>
                        {card.delta}
                      </span>
                    </div>
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {card.label}
                      </p>
                      <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Filters & Table Section */}
            <div className="bg-white rounded-xl border border-[#c3c6d7] shadow-sm overflow-hidden flex flex-col">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    aria-label="Filter academic resources by type"
                    value={typeFilter}
                    onChange={(event) => selectType(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-300 focus:bg-white"
                  >
                    <option value="all">All Resources</option>
                    <option value="book">Books</option>
                    <option value="journal">Journals</option>
                    <option value="course">Courses</option>
                  </select>
                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search resources..."
                    className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
                  />
                  <select
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(1);
                    }}
                    className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
                  >
                    <option value="">All statuses</option>
                    <option value="DRAFT">Draft</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="DISABLED">Disabled</option>
                    <option value="WITHDRAWN">Withdrawn</option>
                  </select>
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value);
                      setPage(1);
                    }}
                    className="bg-[#f2f4f6] border-none rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-[#004ac6]/20"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </div>
              </div>

              {isLoading && (
                <div className="border-b border-slate-200 px-4 py-3 text-sm text-slate-500">
                  Loading academic resources…
                </div>
              )}
              {errorMessage && !isLoading && (
                <div className="border-b border-slate-200 px-4 py-3 text-sm text-red-600">
                  {errorMessage}
                </div>
              )}

              {/* High Density Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-2.5 font-semibold">Resource</th>
                      <th className="px-4 py-2.5 font-semibold">Author / Contributor</th>
                      <th className="px-4 py-2.5 font-semibold">Type</th>
                      <th className="px-4 py-2.5 font-semibold">Status</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Price</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Downloads</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Revenue</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-[#191c1e]">
                    {resources.map((r, idx) => {
                      const TypeIcon = r.typeIcon;
                      return (
                        <tr
                          key={r.id}
                          className={`group cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 ${idx % 2 === 1 ? "bg-slate-50/40" : ""}`}
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-3">
                              <div className="flex h-11 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100 text-slate-400">
                                {r.cover.type === "image" ? (
                                  <img
                                    className="w-full h-full object-cover"
                                    src={r.cover.src}
                                    alt={r.title}
                                  />
                                ) : (
                                  <r.cover.icon className="h-4 w-4" />
                                )}
                              </div>
                              <div>
                                <p className="truncate font-semibold text-[#004ac6] group-hover:underline">
                                  {r.title}
                                </p>
                                <p className="truncate text-[11px] text-slate-400">{r.meta}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">{r.author}</td>
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2">
                              <TypeIcon className="w-3.5 h-3.5" />
                              {r.type}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusStyles[r.status]}`}
                            >
                              {r.status}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-[13px]">
                            {r.price}
                          </td>
                          <td className="px-4 py-2.5 text-right">{r.downloads}</td>
                          <td className="px-4 py-2.5 text-right font-semibold">{r.revenue}</td>
                          <td className="px-4 py-2.5">
                            <button
                              onClick={() => openDetail(r)}
                              title="View details before deciding"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-[#dae2fd] px-2.5 py-1 text-xs font-semibold text-[#004ac6] transition hover:bg-[#c9d6ff]"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary + pagination (the envelope carries `total`/`page`/`totalPages`) */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-3">
                <p className="text-xs text-slate-500">
                  Showing {resources.length} resource{resources.length === 1 ? "" : "s"}
                  {activeTotal > 0 ? ` of ${activeTotal}` : ""}
                  {" "}(page {page} of {Math.max(activePages, 1)})
                </p>
                <StaffPagination
                  page={page}
                  lastPage={Math.max(activePages, 1)}
                  onPageChange={setPage}
                />
              </div>
            </div>

            {/* Recently Added (derived from the live API) */}
            <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">
                Recently Added
              </h2>
              {resources.length === 0 ? (
                <p className="mt-3 text-sm text-slate-400">No resources to show yet.</p>
              ) : (
                <div className="mt-3 divide-y divide-slate-100">
                  {resources.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#dae2fd] text-xs font-bold text-[#131b2e]">
                          {r.type.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="truncate text-sm font-semibold">{r.title}</p>
                          <p className="text-xs text-slate-400">
                            {r.type} • {r.status}
                          </p>
                        </div>
                      </div>
                      <span className="shrink-0 font-mono text-sm font-bold text-[#004ac6]">
                        {r.revenue}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AcademicResources;